type CacheEntry = {
  data: any;
  expiresAt: number;
};

const responseCache = new Map<string, CacheEntry>();
const inFlightRequests = new Map<string, Promise<any>>();
const STORAGE_PREFIX = 'api-cache:v1:';

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
};

const getStorageKey = (cacheKey: string) => `${STORAGE_PREFIX}${cacheKey}`;

const persistEntry = (cacheKey: string, entry: CacheEntry) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(getStorageKey(cacheKey), JSON.stringify(entry));
  } catch {
    // Ignore storage failures (quota/private mode); in-memory cache still works.
  }
};

const readPersistedEntry = (cacheKey: string): CacheEntry | null => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(getStorageKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.expiresAt !== 'number') return null;
    return parsed as CacheEntry;
  } catch {
    return null;
  }
};

const removePersistedEntry = (cacheKey: string) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(getStorageKey(cacheKey));
  } catch {
    // no-op
  }
};

export const getCachedJson = (cacheKey: string) => {
  const now = Date.now();
  const memoryEntry = responseCache.get(cacheKey);
  if (memoryEntry) {
    if (now > memoryEntry.expiresAt) {
      responseCache.delete(cacheKey);
      removePersistedEntry(cacheKey);
      return null;
    }
    return memoryEntry.data;
  }

  const persistedEntry = readPersistedEntry(cacheKey);
  if (!persistedEntry) return null;
  if (now > persistedEntry.expiresAt) {
    removePersistedEntry(cacheKey);
    return null;
  }

  responseCache.set(cacheKey, persistedEntry);
  return persistedEntry.data;
};

export const setCachedJson = (
  cacheKey: string,
  data: any,
  ttlMs: number = DEFAULT_TTL_MS
) => {
  const entry = {
    data,
    expiresAt: Date.now() + ttlMs,
  };
  responseCache.set(cacheKey, entry);
  persistEntry(cacheKey, entry);
};

export const fetchJsonWithCache = async (
  cacheKey: string,
  url: string,
  ttlMs: number = DEFAULT_TTL_MS
) => {
  const cached = getCachedJson(cacheKey);
  if (cached !== null) {
    return cached;
  }

  const existingRequest = inFlightRequests.get(cacheKey);
  if (existingRequest) {
    return existingRequest;
  }

  const requestPromise = (async () => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();
    const entry = {
      data,
      expiresAt: Date.now() + ttlMs
    };
    responseCache.set(cacheKey, entry);
    persistEntry(cacheKey, entry);
    return data;
  })();

  inFlightRequests.set(cacheKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    inFlightRequests.delete(cacheKey);
  }
};

export const clearApiCache = () => {
  responseCache.clear();
  inFlightRequests.clear();
  const storage = getStorage();
  if (!storage) return;
  try {
    const keysToDelete: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => storage.removeItem(key));
  } catch {
    // no-op
  }
};
